import { useState, useEffect } from "react";
import toast from "../../../utils/toast";
import {
  getPGSharingTypes,
  getPGSharingBedPricing,
  createPgSharingTypes,
  updatePgSharingTypes,
  togglePgSharingTypes,
  removePgSharingTypes,
  getPGSharingBedPricingById,
  updatePGSharingBedPricingById,
} from "../../../services/propertyOwner/propertyOwnerServices";

const INITIAL_ADD_FORM = {
  icon: "🛏",
  name: "",
  description: "",
  maxPersons: 1,
};

export default function PgSharingType() {
  const [sharingTypes, setSharingTypes] = useState([]);
  const [bedPricing, setBedPricing] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [pricingLoading, setPricingLoading] = useState(true);

  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenPricingModal, setIsOpenPricingModal] = useState(false);
  const [isOpenDeleteConfirm, setIsOpenDeleteConfirm] = useState(false);

  const [addForm, setAddForm] = useState(INITIAL_ADD_FORM);
  const [addErrors, setAddErrors] = useState({});
  const [addLoading, setAddLoading] = useState(false);

  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [pricingProperty, setPricingProperty] = useState(null);
  const [pricingData, setPricingData] = useState([]);
  const [pricingModalLoading, setPricingModalLoading] = useState(false);
  const [pricingSaveLoading, setPricingSaveLoading] = useState(false);

  useEffect(() => {
    fetchSharingTypes();
    fetchBedPricing();
  }, []);

  const fetchSharingTypes = async () => {
    setListLoading(true);
    try {
      const res = await getPGSharingTypes();
      if (res.statusCode === 1) setSharingTypes(res.data ?? []);
    } catch (err) {
      console.log(
        err.response?.data?.message || "Failed to fetch sharing types",
      );
    } finally {
      setListLoading(false);
    }
  };

  const fetchBedPricing = async () => {
    setPricingLoading(true);
    try {
      const res = await getPGSharingBedPricing();
      if (res.statusCode === 1) setBedPricing(res.data ?? []);
    } catch (err) {
      console.log(err.response?.data?.message || "Failed to fetch bed pricing");
    } finally {
      setPricingLoading(false);
    }
  };

  const groupedPricing = bedPricing.reduce((acc, item) => {
    if (!acc[item.pgPropertyId]) {
      acc[item.pgPropertyId] = {
        pgPropertyName: item.pgPropertyName,
        pgPropertyId: item.pgPropertyId,
        types: [],
      };
    }
    acc[item.pgPropertyId].types.push(item);
    return acc;
  }, {});

  // ── Toggle ──
  const handleToggleStatus = async (id) => {
    try {
      const res = await togglePgSharingTypes(id);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchSharingTypes();
      }
    } catch (err) {
      toast.error("Failed to toggle status.");
    }
  };

  // ── Delete ──
  const openDeleteConfirm = (item) => {
    setDeleteTarget(item);
    setIsOpenDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setIsOpenDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const res = await removePgSharingTypes(deleteTarget.sharingTypeId);
      if (res.statusCode === 1) {
        toast.success(res.message || "Sharing type deleted.");
        closeDeleteConfirm();
        fetchSharingTypes();
        fetchBedPricing();
      } else {
        toast.error(res.message || "Failed to delete.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Add Modal ──
  const openAddModal = () => {
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
    setIsOpenAddModal(true);
  };

  const closeAddModal = () => {
    setIsOpenAddModal(false);
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
  };

  const validateAdd = () => {
    const e = {};
    if (!addForm.name) e.name = "Name is required.";
    if (!addForm.maxPersons) e.maxPersons = "Max persons is required.";
    setAddErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddSubmit = async () => {
    if (!validateAdd()) return;
    setAddLoading(true);
    try {
      const payload = {
        icon: addForm.icon,
        name: addForm.name,
        description: addForm.description,
        maxPersons: Number(addForm.maxPersons),
      };
      const res = await createPgSharingTypes(payload);
      if (res.statusCode === 1) {
        toast.success(res.message || "Sharing type added successfully.");
        closeAddModal();
        fetchSharingTypes();
        fetchBedPricing();
      } else {
        toast.error(res.message || "Failed to add.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit Modal ──
  const openEditModal = (item) => {
    setSelectedType(item);
    setEditForm({
      icon: item.icon || "🛏",
      name: item.name || "",
      description: item.description || "",
      maxPersons: item.maxPersons || 1,
    });
    setEditErrors({});
    setIsOpenEditModal(true);
  };

  const closeEditModal = () => {
    setIsOpenEditModal(false);
    setSelectedType(null);
    setEditForm({});
    setEditErrors({});
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.name) e.name = "Name is required.";
    if (!editForm.maxPersons) e.maxPersons = "Max persons is required.";
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleEditSubmit = async () => {
    if (!validateEdit()) return;
    setEditLoading(true);
    try {
      const payload = {
        icon: editForm.icon,
        name: editForm.name,
        description: editForm.description,
        maxPersons: Number(editForm.maxPersons),
      };
      const res = await updatePgSharingTypes(
        selectedType.sharingTypeId,
        payload,
      );
      if (res.statusCode === 1) {
        toast.success(res.message || "Sharing type updated successfully.");
        closeEditModal();
        fetchSharingTypes();
      } else {
        toast.error(res.message || "Failed to update.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  // ── Pricing Modal ──
  const openPricingModal = async (pg) => {
    setPricingProperty(pg);
    setPricingModalLoading(true);
    setIsOpenPricingModal(true);
    try {
      const res = await getPGSharingBedPricingById(pg.pgPropertyId);
      if (res.statusCode === 1) setPricingData(res.data ?? []);
    } catch (err) {
      toast.error("Failed to load pricing.");
    } finally {
      setPricingModalLoading(false);
    }
  };

  const closePricingModal = () => {
    setIsOpenPricingModal(false);
    setPricingProperty(null);
    setPricingData([]);
  };

  const handlePricingChange = (sharingTypeId, field, value) => {
    setPricingData((prev) =>
      prev.map((item) =>
        item.sharingTypeId === sharingTypeId
          ? { ...item, [field]: Number(value) }
          : item,
      ),
    );
  };

  const handlePricingSubmit = async () => {
    setPricingSaveLoading(true);
    try {
      const payload = {
        pricing: pricingData.map((item) => ({
          sharingTypeId: item.sharingTypeId,
          monthlyRate: item.monthlyRate,
          dailyRate: item.dailyRate,
        })),
      };
      const res = await updatePGSharingBedPricingById(
        pricingProperty.pgPropertyId,
        payload,
      );
      if (res.statusCode === 1) {
        toast.success(res.message || "Pricing saved successfully.");
        closePricingModal();
        fetchBedPricing();
      } else {
        toast.error(res.message || "Failed to save pricing.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setPricingSaveLoading(false);
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Sharing Types Master
            </div>
            <div className="page-sub" id="page-sub">
              Define sharing types and bed-type pricing
            </div>
          </div>
          <div className="topbar-right" id="topbar-right">
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add Sharing Type
            </button>
          </div>
        </div>

        <div className="content" id="content-area">
          <div className="two-col">
            {/* Sharing Types Table */}
            <div className="card">
              <div className="flex-between mb12">
                <div className="card-title" style={{ margin: 0 }}>
                  Sharing Types Master
                </div>
              </div>
              <div className="info-box mb10" style={{ fontSize: 11 }}>
                Define occupancy types. Max Beds controls how many beds are
                assigned together: Single=1, Double=2, Triple=3.
              </div>
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Icon</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Max Beds</th>
                      <th>Active</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 7 }).map((_, j) => (
                            <td key={j}>
                              <div
                                style={{
                                  height: 14,
                                  borderRadius: 6,
                                  background:
                                    "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                  backgroundSize: "200% 100%",
                                  animation: "shimmer 1.4s infinite",
                                  width: "70%",
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : sharingTypes.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          style={{
                            textAlign: "center",
                            color: "var(--text-tertiary)",
                            padding: 24,
                          }}
                        >
                          No sharing types found.
                        </td>
                      </tr>
                    ) : (
                      sharingTypes.map((s, idx) => (
                        <tr key={s.sharingTypeId}>
                          <td className="sn-col">{idx + 1}</td>
                          <td style={{ fontSize: 20, textAlign: "center" }}>
                            {s.icon}
                          </td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{s.name}</div>
                          </td>
                          <td
                            style={{
                              fontSize: 12,
                              color: "var(--text-secondary)",
                            }}
                          >
                            {s.description || "—"}
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: 13,
                              fontWeight: 700,
                            }}
                          >
                            {s.maxPersons}
                          </td>
                          <td>
                            <label className="toggle">
                              <input
                                type="checkbox"
                                checked={s.isActive}
                                onChange={() =>
                                  handleToggleStatus(s.sharingTypeId)
                                }
                              />
                              <span className="toggle-slider" />
                            </label>
                          </td>
                          <td>
                            <div className="td-actions">
                              <span
                                className="action-icon"
                                onClick={() => openEditModal(s)}
                              >
                                <span className="tip">Edit</span>✎
                              </span>
                              <span
                                className="action-icon red"
                                onClick={() => openDeleteConfirm(s)}
                              >
                                <span className="tip">Delete</span>🗑
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bed Type Pricing Table */}
            <div className="card">
              <div className="card-title">
                Bed Type Pricing (per PG Property)
              </div>
              <div className="info-box mb10" style={{ fontSize: 11 }}>
                Pricing is per <strong>physical bed type</strong>{" "}
                (Single/Double). Set the monthly rent for each PG property.
              </div>
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>PG Property</th>
                      <th>Sharing Type</th>
                      <th>Monthly</th>
                      <th>Daily</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingLoading ? (
                      Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 5 }).map((_, j) => (
                            <td key={j}>
                              <div
                                style={{
                                  height: 14,
                                  borderRadius: 6,
                                  background:
                                    "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                  backgroundSize: "200% 100%",
                                  animation: "shimmer 1.4s infinite",
                                  width: "70%",
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : Object.keys(groupedPricing).length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            textAlign: "center",
                            color: "var(--text-tertiary)",
                            padding: 24,
                          }}
                        >
                          No pricing data found.
                        </td>
                      </tr>
                    ) : (
                      Object.values(groupedPricing).map((pg) =>
                        pg.types.map((item, i) => (
                          <tr key={`${pg.pgPropertyId}-${item.sharingTypeId}`}>
                            {i === 0 && (
                              <td
                                rowSpan={pg.types.length}
                                style={{
                                  fontWeight: 600,
                                  fontSize: 12,
                                  verticalAlign: "middle",
                                }}
                              >
                                {pg.pgPropertyName}
                              </td>
                            )}
                            <td style={{ fontSize: 12 }}>
                              {item.sharingTypeName}
                            </td>
                            <td style={{ fontWeight: 600, fontSize: 13 }}>
                              ₹{item.monthlyRate?.toLocaleString("en-IN")}/mo
                            </td>
                            <td style={{ fontWeight: 600, fontSize: 13 }}>
                              ₹{item.dailyRate?.toLocaleString("en-IN")}/day
                            </td>
                            {i === 0 && (
                              <td
                                rowSpan={pg.types.length}
                                style={{ verticalAlign: "middle" }}
                              >
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => openPricingModal(pg)}
                                >
                                  ₹ Set Pricing
                                </button>
                              </td>
                            )}
                          </tr>
                        )),
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Modal ── */}
      {isOpenAddModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 500, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Add Sharing Type</div>
              <button className="modal-close" onClick={closeAddModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid mb12">
                <div className="form-group">
                  <label>Sharing Type Name *</label>
                  <input
                    type="text"
                    value={addForm.name}
                    onChange={(e) => {
                      setAddForm((p) => ({ ...p, name: e.target.value }));
                      setAddErrors((p) => ({ ...p, name: "" }));
                    }}
                    placeholder="e.g. Single, Double, Triple"
                    style={addErrors.name ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.name && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.name}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Icon (emoji)</label>
                  <input
                    type="text"
                    value={addForm.icon}
                    onChange={(e) =>
                      setAddForm((p) => ({ ...p, icon: e.target.value }))
                    }
                    placeholder="🛏"
                  />
                </div>
                <div className="form-group">
                  <label>Max Persons *</label>
                  <input
                    type="number"
                    value={addForm.maxPersons}
                    min={1}
                    max={10}
                    onChange={(e) => {
                      setAddForm((p) => ({ ...p, maxPersons: e.target.value }));
                      setAddErrors((p) => ({ ...p, maxPersons: "" }));
                    }}
                    style={
                      addErrors.maxPersons ? { borderColor: "#dc2626" } : {}
                    }
                  />
                  <div className="input-hint">Single=1, Double=2, Triple=3</div>
                  {addErrors.maxPersons && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.maxPersons}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={addForm.description}
                    onChange={(e) =>
                      setAddForm((p) => ({ ...p, description: e.target.value }))
                    }
                    placeholder="Brief description"
                  />
                </div>
              </div>
              <div className="info-box" style={{ fontSize: 12 }}>
                💡 Max Beds controls how many consecutive beds are assigned per
                tenant during bed assignment.
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeAddModal}
                disabled={addLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddSubmit}
                disabled={addLoading}
              >
                {addLoading ? "Saving..." : "💾 Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {isOpenEditModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 500, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">
                Edit Sharing Type — {selectedType?.name}
              </div>
              <button className="modal-close" onClick={closeEditModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid mb12">
                <div className="form-group">
                  <label>Sharing Type Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => {
                      setEditForm((p) => ({ ...p, name: e.target.value }));
                      setEditErrors((p) => ({ ...p, name: "" }));
                    }}
                    placeholder="e.g. Single, Double, Triple"
                    style={editErrors.name ? { borderColor: "#dc2626" } : {}}
                  />
                  {editErrors.name && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.name}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Icon (emoji)</label>
                  <input
                    type="text"
                    value={editForm.icon}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, icon: e.target.value }))
                    }
                    placeholder="🛏"
                  />
                </div>
                <div className="form-group">
                  <label>Max Persons *</label>
                  <input
                    type="number"
                    value={editForm.maxPersons}
                    min={1}
                    max={10}
                    onChange={(e) => {
                      setEditForm((p) => ({
                        ...p,
                        maxPersons: e.target.value,
                      }));
                      setEditErrors((p) => ({ ...p, maxPersons: "" }));
                    }}
                    style={
                      editErrors.maxPersons ? { borderColor: "#dc2626" } : {}
                    }
                  />
                  <div className="input-hint">Single=1, Double=2, Triple=3</div>
                  {editErrors.maxPersons && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.maxPersons}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Brief description"
                  />
                </div>
              </div>
              <div className="info-box" style={{ fontSize: 12 }}>
                💡 Max Beds controls how many consecutive beds are assigned per
                tenant during bed assignment.
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEditSubmit}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "💾 Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation ── */}
      {isOpenDeleteConfirm && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 420, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Delete Sharing Type</div>
              <button className="modal-close" onClick={closeDeleteConfirm}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                Are you sure you want to delete{" "}
                <strong>{deleteTarget?.name}</strong>?
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                This action cannot be undone. All pricing linked to this sharing
                type will also be removed.
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeDeleteConfirm}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{
                  background: "var(--red, #dc2626)",
                  color: "#fff",
                  border: "none",
                }}
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "🗑 Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pricing Modal ── */}
      {isOpenPricingModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 620, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">₲ Bed Type Pricing</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {pricingProperty?.pgPropertyName}
                </div>
              </div>
              <button className="modal-close" onClick={closePricingModal}>
                ✕
              </button>
            </div>
            <div>
              <div className="info-box" style={{ margin: 14, fontSize: 12 }}>
                Set monthly and daily rent per bed type. Tenant billing mode
                (Monthly/Daily) is set individually in the Tenant List.
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr 1fr",
                  gap: 10,
                  padding: "10px 16px 6px",
                  background: "#f8fafc",
                  borderBottom: "0.5px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Bed Type
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Monthly Charge
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Daily Charge
                </div>
              </div>
              {pricingModalLoading ? (
                <div
                  style={{
                    padding: "24px 0",
                    textAlign: "center",
                    color: "var(--text-tertiary)",
                    fontSize: 13,
                  }}
                >
                  Loading...
                </div>
              ) : (
                pricingData.map((item) => (
                  <div
                    key={item.sharingTypeId}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "140px 1fr 1fr",
                      gap: 10,
                      alignItems: "center",
                      padding: "14px 16px",
                      borderBottom: "0.5px solid #e5e7eb",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {item.sharingTypeName}
                      </div>
                      <div
                        style={{ fontSize: 10, color: "var(--text-tertiary)" }}
                      >
                        Max {item.maxPersons} person(s)
                      </div>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                      <input
                        type="number"
                        value={item.monthlyRate}
                        min={0}
                        onChange={(e) =>
                          handlePricingChange(
                            item.sharingTypeId,
                            "monthlyRate",
                            e.target.value,
                          )
                        }
                        style={{
                          flex: 1,
                          fontSize: 12,
                          padding: "7px 9px",
                          border: "0.5px solid #d1d5db",
                          borderRadius: 6,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          whiteSpace: "nowrap",
                        }}
                      >
                        /mo
                      </span>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                      <input
                        type="number"
                        value={item.dailyRate}
                        min={0}
                        onChange={(e) =>
                          handlePricingChange(
                            item.sharingTypeId,
                            "dailyRate",
                            e.target.value,
                          )
                        }
                        style={{
                          flex: 1,
                          fontSize: 12,
                          padding: "7px 9px",
                          border: "0.5px solid #d1d5db",
                          borderRadius: 6,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          whiteSpace: "nowrap",
                        }}
                      >
                        /day
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={closePricingModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handlePricingSubmit}
                disabled={pricingSaveLoading}
              >
                {pricingSaveLoading ? "Saving..." : "💾 Save Pricing"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
